export type FluencyOption = {
  value: 'Spoken' | 'Written';
  label: string;
};

export interface PaymentRecord {
  name?: string;
  toAgent?: {
    currency?: string;
    amount?: number;
    payType?: 'hourly' | 'fixed' | 'ticket_based';
  };
  fromClient?: {
    currency?: string;
    amount?: number;
    payType?: 'hourly' | 'fixed' | 'ticket_based';
  };
  effectiveStartDate?: string;
  effectiveEndDate?: string;
}

// For backward compatibility - old entries have Payment as object, new entries as array
export type Payment = PaymentRecord | PaymentRecord[];

export type BonusFrequency =
  | 'monthly'
  | 'weekly'
  | 'daily'
  | 'fortnightly'
  | 'one-time'
  | 'specific_day_of_week'
  | 'specific_day_of_month';

// Matches backend bonusSchema exactly
export interface BonusRecord {
  name?: string;
  amount?: number;
  amountFromClient?: number;
  frequency?: BonusFrequency;
  paymentType?: 'hourly' | 'fixed' | 'ticket_based';
  dayOfWeek?: number;
  dayOfMonth?: number;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
}

export type RecordArrayKey = 'payment' | 'bonus' | 'otherPayments';

interface TimeSlot {
  _id?: string;
  startTime: string;
  endTime: string;
  workingHours: number;
}

interface Language {
  language: string;
  fluency: ('Spoken' | 'Written')[];
}

interface PhoneNumber {
  countryCode: string;
  number: string;
  countryCodeEnum: string;
}

interface WorkSchedule {
  timeSlots: {
    [key: string]: TimeSlot[];
  };
  breakDuration?: {
    value: number;
    unit: 'minutes' | 'hours';
  };
  tolerancePeriod?: number;
}

export interface IAgent {
  _id?: string;
  kimId?: string;
  name: string;
  dob?: string | null;
  whatsappNumber?: PhoneNumber;
  governmentId?: string;
  emergencyContact?: {
    name: string;
    phoneNumber: String;
  };
  currentAddress?: string;
  permanentAddress?: string;
  pastEmployers?: {
    employerName: string;
    employerUrl: string;
    startDate: string;
    endDate: string;
  }[];
  personalEmail: string;
  phoneNumber: PhoneNumber;
  profileUri?: string;
  resumeUri?: string;
  applicationVideoUri?: string;
  hobbies?: string[];
  upworkProfile?: string;
  totalExperience?: string | number;
  status: 'active' | 'inactive' | 'on_leave' | 'terminated';
  skills?: string[];
  languages?: Language[];
  tools?: string[];
  channels?: string[];
  monthlyExpectedWorkHours?: number;
  industry?: string;
  relevantIndustries?: string[];
  bankDetails?: {
    bankName?: string;
    name?: string;
    accountNumber?: string;
    accountType?: string;
    bankCode?: string;
    branchName?: string;
    city?: string;
    bankStatement?: string;
  };
  paymentIds?: {
    paypalId?: string;
    payoneerId?: string;
    wiseId?: string;
  };
  preferredPaymentMethod?: 'paypal' | 'payoneer' | 'wise' | 'bank';
  comments?: string;
  passportNumber?: string;
  createdAt?: Date;
  updatedAt?: Date;
  // Computed by getAgentList from AgentBrand collection — not stored on the model
  brands?: { brandId: string; timezone: string }[];
}

export interface IAgentBrand {
  brandId: string;
  agentId: string;
  email: string;
  password: string;
  payment: Payment;
  bonus: BonusRecord[];
  otherPayments?: PaymentRecord[];
  workSchedule: WorkSchedule;
  leaveBalance?: {
    provider: 'Brand' | 'Kim';
    totalLeaves: number;
    leaveTypes: Record<
      string,
      {
        monthlyAccruals: Array<{
          month: string;
          accrued: number;
          consumed: number;
          expiryDate: string;
          status: 'active' | 'expired';
        }>;
      }
    >;
    pendingRequests?: Array<{
      leaveRequestId: string;
      hours: number;
      leaveType: string;
      createdAt: string;
    }>;
  };
  trialPeriod: {
    days: number;
    startDate: string;
    status: 'active' | 'completed' | 'pending';
  };
  timezone: string;
  captureTimeInterval: number;
  tolerancePeriod?: number;
  isActive?: boolean;
  onboardingDate?: string;
  offboardingDate?: string;
  upworkContractId?: string;
  upworkFreelancerId?: string;
  isUpwork: boolean;
  overdueSessionThresholdMinutes?: number;
  idleTimeThresholdMinutes?: number;
  monthlyExpectedWorkHours?: number;
  enableLeaveFeature?: boolean;
  slackAlertChannels?: string[];
}
export type PaymentMethod = NonNullable<IAgent['preferredPaymentMethod']>;

export const invalidAgentStatusesForInvoiceGeneration = ['inactive'];
